/**
 * Mailbox usage: the mock behind `GET /domains/{domain}/accounts/{email}/usage`.
 *
 * TEMPORARY, like the seed beside it: there is no provider backend yet. The list endpoint returns no
 * usage, so the seed's usage lives here, keyed by the same addresses {@link buildMailboxes} builds
 * for the same variant and domain. When the real endpoint lands, the read below is what gets
 * swapped; its callers already treat every optional field as "unknown when absent".
 *
 * The mock has no server-side memory: an address that is not in the seed reads as a fresh mailbox
 * (nothing stored, nothing received), and a seed address that was deleted and created again reads
 * its seed usage again. The real service starts a re-created mailbox at zero.
 */
import {
  DEFAULT_MAILBOX_QUOTA_BYTES,
  MAILBOX_VARIANT_COUNT,
  daysAgo,
  hoursAgo,
  mailboxEmail,
  type DomainStatus,
  type MailboxError,
  type MailboxRecord,
  type MailboxUsage,
} from './mailbox-records';

/** One gibibyte in bytes, the unit the seed's figures are written in. */
const GIB = 1024 ** 3;

/** A usage reading with a ceiling: `bytesRemaining` follows from the other two, as the provider's does. */
const capped = (email: string, bytesUsed: number, rest: Omit<MailboxUsage, 'email' | 'bytesUsed'>): MailboxUsage => ({
  email,
  bytesUsed,
  bytesMax: DEFAULT_MAILBOX_QUOTA_BYTES,
  bytesRemaining: Math.max(0, DEFAULT_MAILBOX_QUOTA_BYTES - bytesUsed),
  ...rest,
});

/**
 * The seed usage for a brand at cycle position `variant`, on `domain`, keyed by address. Variant 4's
 * `deleted` mailbox has no entry: a deleted mailbox has no usage to read (see
 * {@link readMockMailboxUsage}), which the UI renders as unavailable. Variant 5's domain has failed,
 * so its mailbox has none either (the read fails). Every seeded mailbox reports its ceiling, so every
 * card draws the same storage block; variant 3's paid mailbox reports no unread flag, so that
 * "absent means unknown" branch renders from seed data.
 */
export const buildMailboxUsage = (variant: number, domain: string): Record<string, MailboxUsage> => {
  const v = ((variant % MAILBOX_VARIANT_COUNT) + MAILBOX_VARIANT_COUNT) % MAILBOX_VARIANT_COUNT;
  const hello = mailboxEmail('hello', domain);
  // support@ (the system mailbox) carries the casino's CRM mail: a steady trickle, on every variant
  // that lists it on a working domain.
  const support = capped(mailboxEmail('support', domain), Math.round(0.3 * GIB), {
    messagesUsed: 410,
    unreadMail: false,
    lastReceivedAt: hoursAgo(1),
  });
  const entries: MailboxUsage[] = (() => {
    switch (v) {
      case 1:
        return [
          support,
          capped(hello, Math.round(0.8 * GIB), {
            messagesUsed: 1240,
            unreadMail: true,
            lastReceivedAt: hoursAgo(2),
            smtpSent: 12,
            smtpMax: 500,
          }),
        ];
      case 2: {
        const archive = mailboxEmail('archive', domain);
        return [
          support,
          capped(hello, Math.round(1.4 * GIB), { messagesUsed: 3120, unreadMail: false, lastReceivedAt: hoursAgo(2) }),
          // 93% of the quota, past the storage warning ratio. A ratio of the quota, not a fixed
          // figure, so the warning survives a change of the default size.
          capped(archive, Math.round(0.93 * DEFAULT_MAILBOX_QUOTA_BYTES), {
            messagesUsed: 18900,
            unreadMail: true,
            lastReceivedAt: hoursAgo(9),
          }),
        ];
      }
      case 3:
        return [
          support,
          capped(hello, Math.round(1.0 * GIB), { messagesUsed: 900, unreadMail: false, lastReceivedAt: hoursAgo(2) }),
          // The paid mailbox reports its ceiling like every other seeded mailbox, so its card draws
          // the same storage block as an ordinary one. No unread flag reported: ABSENT, not false. (The
          // no-ceiling treatment, "used" and no bar, is covered by MailboxTile's own spec, not here.)
          capped(mailboxEmail('team', domain), Math.round(2.1 * GIB), {
            messagesUsed: 5400,
            lastReceivedAt: hoursAgo(4),
          }),
        ];
      case 4:
        return [
          support,
          capped(hello, Math.round(0.5 * GIB), { messagesUsed: 640, unreadMail: false, lastReceivedAt: hoursAgo(2) }),
          capped(mailboxEmail('sales', domain), Math.round(3.6 * GIB), {
            messagesUsed: 7800,
            lastReceivedAt: daysAgo(4),
          }),
        ];
      // Variant 5's domain has FAILED: there is no mail service to read usage from, so its mailbox
      // has no entry and {@link fetchMockMailboxUsage} rejects for it (the tile's failure path).
      case 5:
      case 0:
      default:
        return [];
    }
  })();
  return Object.fromEntries(entries.map((usage) => [usage.email, usage]));
};

/**
 * A mailbox created this session: nothing stored, nothing received, the default ceiling, and the
 * send cap a new mailbox commonly starts on. No `unreadMail` and no `lastReceivedAt`: it has never
 * received, so there is nothing to report either way.
 */
export const freshMailboxUsage = (email: string): MailboxUsage => ({
  email,
  bytesUsed: 0,
  bytesMax: DEFAULT_MAILBOX_QUOTA_BYTES,
  bytesRemaining: DEFAULT_MAILBOX_QUOTA_BYTES,
  messagesUsed: 0,
  smtpSent: 0,
  smtpMax: 25,
});

/** What the usage read was asked for. `lastReceived` mirrors `?lastReceived=true`. */
export type MailboxUsageQuery = { lastReceived?: boolean };

/**
 * One mailbox's usage, as the endpoint would answer: `null` for a `deleted` mailbox, which has none
 * to read; any other address missing from the seed reads as fresh. `lastReceivedAt` is present only when asked for, exactly
 * as the real query parameter gates it.
 */
export const readMockMailboxUsage = (
  seed: Readonly<Record<string, MailboxUsage>>,
  mailbox: Pick<MailboxRecord, 'email' | 'status'>,
  query: MailboxUsageQuery = {},
): MailboxUsage | null => {
  if (mailbox.status === 'deleted') return null;
  const found = Object.prototype.hasOwnProperty.call(seed, mailbox.email) ? seed[mailbox.email] : null;
  const usage = found ?? freshMailboxUsage(mailbox.email);
  if (query.lastReceived) return usage;
  const { lastReceivedAt: _omitted, ...rest } = usage;
  void _omitted;
  return rest;
};

/**
 * How long a mock usage read takes. Long enough that the tile's skeleton is actually seen in dev;
 * the real read costs the service two upstream round trips (three with `lastReceived`).
 */
export const MAILBOX_USAGE_MOCK_DELAY_MS = 600;

/**
 * The usage read as the page will make it: asynchronous, after {@link MAILBOX_USAGE_MOCK_DELAY_MS}.
 * Resolves `null` for a `deleted` mailbox (none to read) and rejects on a `failed` domain, where the
 * provider has no mail service to answer: with a {@link MailboxError}, `upstream_failed` and the
 * provider's words in `upstream`, as the real endpoint's `502` does. When the endpoint lands, this
 * is the function the fetch replaces.
 */
export const fetchMockMailboxUsage = (
  seed: Readonly<Record<string, MailboxUsage>>,
  mailbox: Pick<MailboxRecord, 'email' | 'status'>,
  { query = {}, domainStatus }: { query?: MailboxUsageQuery; domainStatus: DomainStatus | undefined },
): Promise<MailboxUsage | null> =>
  new Promise((resolve, reject) => {
    setTimeout(() => {
      if (domainStatus === 'failed') {
        const error: MailboxError = {
          code: 'upstream_failed',
          message: `could not read usage for ${mailbox.email}: the mail provider has no service for this domain`,
          upstream: 'order not found',
        };
        reject(error);
      } else resolve(readMockMailboxUsage(seed, mailbox, query));
    }, MAILBOX_USAGE_MOCK_DELAY_MS);
  });
